"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { Star, MapPin, Clock, Music, Search, Filter } from "lucide-react";
import { InstructorModal } from "../../components/lessons/InstructorModal";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { db } from "../../lib/api/firebase-ultra-safe";
import { collection, getDocs } from "firebase/firestore";
import { getFirebaseAuth } from "@/lib/api/firebase-ultra-safe";
import {
  isAllowedLessonInstructor,
  resolvePublicLessonInstructorName,
} from "@/lib/config/lessonInstructors";
import {
  LESSON_INSTRUCTOR_PROFILES,
  mapSeedToLessonInstructor,
  mergeLessonInstructorProfile,
} from "@/lib/config/lessonInstructorProfiles";
import {
  formatLessonPriceLabel,
  getMonthlyLessonTotalKrw,
} from "@/lib/lesson/lessonPricing";
import {
  getLessonKakaoInquiryMessage,
  isLessonConsultationOnly,
  LESSON_PRICE_DISPLAY_LABEL,
  openLessonKakaoInquiry,
} from "@/lib/config/lessonBooking";
import type { SlotOption } from "@/lib/scheduling/types";
import { formatDateKeySeoul } from "@/lib/scheduling/slotMath";

interface LessonType {
  id: string;
  name: string;
  type: string;
  duration: number;
  price?: number;
  pricePerSession?: number;
  monthlyOptions?: Array<{ sessions: number; discount: number; label: string }>;
  priceNegotiable?: boolean;
  description: string;
}

interface Instructor {
  id: string;
  name: string;
  photo: string;
  specialty: string;
  instruments: string[];
  pricePerHour: number;
  rating: number;
  reviewCount: number;
  location: string;
  experience: string;
  bio: string;
  tagline?: string;
  certifications: string[];
  tags: string[];
  availability: string[];
  lessonTypes: LessonType[];
  userId?: string;
  reviews?: Array<{
    userName: string;
    rating: number;
    comment: string;
    date: string;
    verified?: boolean;
    beta?: boolean;
  }>;
}

export default function LessonsPage() {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInstrument, setSelectedInstrument] = useState<string>("전체");
  const [priceRange, setPriceRange] = useState<string>("전체");
  const [sortBy, setSortBy] = useState<string>("추천순");
  const [selectedInstructor, setSelectedInstructor] =
    useState<Instructor | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [desiredDate, setDesiredDate] = useState("");
  const [desiredTime, setDesiredTime] = useState("");
  const [matchingInstructorIds, setMatchingInstructorIds] = useState<
    Set<string> | null
  >(null);
  const [nextSlotByInstructor, setNextSlotByInstructor] = useState<
    Record<string, string>
  >({});
  const [holding, setHolding] = useState(false);
  const openedFromQueryRef = useRef(false);

  const openInstructor = async (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setIsModalOpen(true);

    try {
      const response = await fetch(
        `/api/instructors/${encodeURIComponent(instructor.id)}/reviews`
      );
      const result = (await response.json().catch(() => null)) as {
        success?: boolean;
        data?: Instructor["reviews"];
      } | null;
      if (!response.ok || !result?.success || !Array.isArray(result.data)) {
        return;
      }
      setSelectedInstructor(current =>
        current?.id === instructor.id
          ? { ...current, reviews: result.data }
          : current
      );
    } catch (error) {
      console.error("강사 후기 로드 실패:", error);
    }
  };

  // Firestore에서 강사 목록 불러오기
  useEffect(() => {
    const loadInstructors = async () => {
      try {
        const instructorsRef = (collection as any)(db, "instructors");
        const snapshot = await (getDocs as any)(instructorsRef);

        const instructorsList: Instructor[] = snapshot.docs
          .map((doc: { id: string; data: () => Record<string, unknown> }) => {
            const raw = doc.data() as Record<string, unknown>;
            const name = (raw.name as string) || "";
            const data = mergeLessonInstructorProfile(name, raw);
            const seed = LESSON_INSTRUCTOR_PROFILES[name];
            return {
              id: doc.id,
              name,
              photo:
                (data.photoUrl as string) ||
                seed?.photoUrl ||
                "/instructors/yang-taewook-placeholder.svg",
              specialty: Array.isArray(data.specialty)
                ? (data.specialty as string[]).join(", ")
                : typeof data.specialty === "string"
                  ? data.specialty
                  : "",
              instruments: Array.isArray(data.specialty)
                ? (data.specialty as string[])
                : [],
              pricePerHour:
                Array.isArray(data.lessonTypes) && data.lessonTypes.length > 0
                  ? (
                      data.lessonTypes[0] as {
                        price?: number;
                        pricePerSession?: number;
                      }
                    )?.price ||
                    (
                      data.lessonTypes[0] as {
                        price?: number;
                        pricePerSession?: number;
                      }
                    )?.pricePerSession ||
                    0
                  : 0,
              rating: (data.rating as number) || 0,
              reviewCount: (data.reviewCount as number) || 0,
              location: (data.location as string) || "",
              experience: (data.experience as string) || "",
              bio: (data.bio as string) || "",
              tagline: (data.tagline as string) || seed?.tagline,
              certifications: Array.isArray(data.certifications)
                ? (data.certifications as string[])
                : [],
              tags: Array.isArray(data.tags)
                ? (data.tags as string[])
                : seed?.tags || [],
              availability: Array.isArray(data.availability)
                ? (data.availability as string[])
                : [],
              lessonTypes: Array.isArray(data.lessonTypes)
                ? (data.lessonTypes as LessonType[])
                : [],
              userId: (data.userId as string) || doc.id,
              reviews: [],
            };
          })
          .filter((instructor: Instructor) =>
            isAllowedLessonInstructor(instructor.name)
          );

        const existingNames = new Set(instructorsList.map(i => i.name));
        for (const seed of Object.values(LESSON_INSTRUCTOR_PROFILES)) {
          if (
            isAllowedLessonInstructor(seed.name) &&
            !existingNames.has(seed.name)
          ) {
            instructorsList.push(mapSeedToLessonInstructor(seed));
          }
        }

        setInstructors(instructorsList);
      } catch (error) {
        console.error("❌ 강사 목록 로드 실패:", error);
        toast.error("강사 목록을 불러오는데 실패했습니다.");
        setInstructors([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadInstructors();
  }, []);

  useEffect(() => {
    if (isLoading || openedFromQueryRef.current || instructors.length === 0) {
      return;
    }
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rawPro = params.get("pro")?.trim();
    const instructorParam = params.get("instructor")?.trim();
    if (!rawPro && !instructorParam) return;

    const pro = resolvePublicLessonInstructorName(rawPro);
    const match =
      instructors.find(
        item =>
          item.name === pro ||
          (instructorParam &&
            isAllowedLessonInstructor(item.name) &&
            (item.id === instructorParam || item.userId === instructorParam))
      ) || instructors.find(item => item.name === pro);
    if (!match) return;
    openedFromQueryRef.current = true;
    void openInstructor(match);
  }, [instructors, isLoading]);

  // 원하는 시간으로 강사 필터 / 다음 가능 시간 표시
  useEffect(() => {
    let cancelled = false;
    const loadSlots = async () => {
      try {
        const params = new URLSearchParams({
          service: "lesson",
          durationMinutes: "60",
        });
        if (desiredDate) params.set("date", desiredDate);
        const res = await fetch(`/api/scheduling/slots?${params.toString()}`, {
          cache: "no-store",
        });
        const data = await res.json();
        if (!res.ok || cancelled) return;
        const slots = (data.slots || []) as SlotOption[];
        const nextMap: Record<string, string> = {};
        for (const slot of slots) {
          if (!nextMap[slot.instructorId]) {
            nextMap[slot.instructorId] = `${slot.date} ${slot.time}`;
          }
        }
        setNextSlotByInstructor(nextMap);

        if (desiredDate && desiredTime) {
          const matched = new Set(
            slots
              .filter(s => s.date === desiredDate && s.time === desiredTime)
              .map(s => s.instructorId)
          );
          setMatchingInstructorIds(matched);
        } else if (desiredDate) {
          setMatchingInstructorIds(new Set(Object.keys(nextMap)));
        } else {
          setMatchingInstructorIds(null);
        }
      } catch (error) {
        console.warn("슬롯 필터 로드 실패:", error);
      }
    };
    void loadSlots();
    return () => {
      cancelled = true;
    };
  }, [desiredDate, desiredTime]);

  // 필터링된 강사 목록
  const filteredInstructors = instructors.filter(instructor => {
    const matchesSearch =
      instructor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      instructor.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      instructor.tags.some(tag =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesInstrument =
      selectedInstrument === "전체" ||
      instructor.instruments.includes(selectedInstrument);

    const matchesPrice =
      priceRange === "전체" ||
      (priceRange === "~40000" && instructor.pricePerHour <= 40000) ||
      (priceRange === "40000~50000" &&
        instructor.pricePerHour > 40000 &&
        instructor.pricePerHour <= 50000) ||
      (priceRange === "50000~60000" &&
        instructor.pricePerHour > 50000 &&
        instructor.pricePerHour <= 60000) ||
      (priceRange === "60000~" && instructor.pricePerHour > 60000);

    const matchesSchedule =
      !matchingInstructorIds || matchingInstructorIds.has(instructor.id);

    return (
      matchesSearch && matchesInstrument && matchesPrice && matchesSchedule
    );
  });

  const timeOptions = useMemo(() => {
    const options: string[] = [];
    for (let min = 9 * 60; min <= 22 * 60; min += 30) {
      const h = String(Math.floor(min / 60)).padStart(2, "0");
      const m = String(min % 60).padStart(2, "0");
      options.push(`${h}:${m}`);
    }
    return options;
  }, []);

  const minDate = formatDateKeySeoul(new Date());

  // 정렬
  const sortedInstructors = [...filteredInstructors].sort((a, b) => {
    if (sortBy === "추천순") {
      return b.rating * b.reviewCount - a.rating * a.reviewCount;
    } else if (sortBy === "평점순") {
      return b.rating - a.rating;
    } else if (sortBy === "리뷰순") {
      return b.reviewCount - a.reviewCount;
    } else if (sortBy === "낮은가격순") {
      return a.pricePerHour - b.pricePerHour;
    } else if (sortBy === "높은가격순") {
      return b.pricePerHour - a.pricePerHour;
    }
    return 0;
  });

  const handleBookLesson = async (
    instructor: Instructor,
    lessonType: LessonType,
    slot: SlotOption
  ) => {
    if (isLessonConsultationOnly()) {
      toast(getLessonKakaoInquiryMessage(), { icon: "💬", duration: 6000 });
      openLessonKakaoInquiry();
      return;
    }

    setHolding(true);
    try {
      const token = await getFirebaseAuth()?.currentUser?.getIdToken();
      if (!token) {
        router.push(
          `/auth/login?callbackUrl=${encodeURIComponent("/lessons")}`
        );
        return;
      }
      const price =
        Number(lessonType.pricePerSession ?? lessonType.price) ||
        instructor.pricePerHour ||
        0;
      const res = await fetch("/api/lessons/bookings/hold", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          instructorId: instructor.id,
          lessonTypeId: lessonType.id || lessonType.name,
          lessonTypeName: lessonType.name,
          durationMinutes: lessonType.duration || 60,
          priceKrw: price,
          date: slot.date,
          time: slot.time,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "예약 생성 실패");

      sessionStorage.setItem(
        "lessonBookingHold",
        JSON.stringify({ booking: data.booking })
      );
      toast.success("시간이 임시 확보되었습니다. 결제를 진행해 주세요.");
      router.push(`/lessons/payment?bookingId=${data.booking.id}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "예약에 실패했습니다."
      );
    } finally {
      setHolding(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">강사 목록 로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold mb-4">개인 레슨</h1>
          <p className="text-lg text-blue-100">
            전문 강사와 함께 음악 실력을 향상시켜보세요
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 검색 및 필터 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* 검색 */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="강사 이름, 전문 분야, 태그 검색..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* 악기 필터 */}
            <div>
              <select
                value={selectedInstrument}
                onChange={e => setSelectedInstrument(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="전체">모든 악기</option>
                <option value="보컬">보컬</option>
                <option value="피아노">피아노</option>
                <option value="기타">기타</option>
                <option value="드럼">드럼</option>
                <option value="베이스">베이스</option>
                <option value="색소폰">색소폰</option>
              </select>
            </div>

            {/* 가격 필터 */}
            <div>
              <select
                value={priceRange}
                onChange={e => setPriceRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="전체">모든 가격</option>
                <option value="~40000">~40,000원</option>
                <option value="40000~50000">40,000~50,000원</option>
                <option value="50000~60000">50,000~60,000원</option>
                <option value="60000~">60,000원~</option>
              </select>
            </div>
          </div>

          {/* 원하는 일정 필터 */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                원하는 날짜
              </label>
              <input
                type="date"
                min={minDate}
                value={desiredDate}
                onChange={e => setDesiredDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                원하는 시간
              </label>
              <select
                value={desiredTime}
                onChange={e => setDesiredTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">전체 시간</option>
                {timeOptions.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  setDesiredDate("");
                  setDesiredTime("");
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                일정 필터 초기화
              </button>
            </div>
          </div>

          {/* 정렬 */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              총{" "}
              <span className="font-semibold">{sortedInstructors.length}</span>
              명의 강사
              {holding ? " · 예약 준비 중..." : ""}
            </p>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="추천순">추천순</option>
              <option value="평점순">평점순</option>
              <option value="리뷰순">리뷰순</option>
              <option value="낮은가격순">낮은 가격순</option>
              <option value="높은가격순">높은 가격순</option>
            </select>
          </div>
        </div>

        {/* 강사 목록 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedInstructors.map(instructor => (
            <button
              key={instructor.id}
              onClick={() => openInstructor(instructor)}
              className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden group text-left w-full"
            >
              {/* 프로필 사진 */}
              <div className="relative h-48 overflow-hidden bg-gray-200">
                <img
                  src={instructor.photo}
                  alt={instructor.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 right-3 bg-white px-3 py-1.5 rounded-full text-xs font-semibold text-gray-700 shadow-md">
                  {isLessonConsultationOnly()
                    ? LESSON_PRICE_DISPLAY_LABEL
                    : (() => {
                        const monthlyLesson = instructor.lessonTypes.find(
                          (lt: LessonType) => lt.type === "monthly"
                        );
                        if (monthlyLesson) {
                          const monthlyTotal =
                            getMonthlyLessonTotalKrw(monthlyLesson);
                          if (monthlyTotal) {
                            return `${monthlyTotal.toLocaleString()}원/월`;
                          }
                        }
                        const pricedLesson = instructor.lessonTypes.find(
                          (lt: LessonType) =>
                            (typeof lt.price === "number" && lt.price > 0) ||
                            (typeof lt.pricePerSession === "number" &&
                              lt.pricePerSession > 0)
                        );
                        if (pricedLesson) {
                          return formatLessonPriceLabel(pricedLesson);
                        }
                        if (
                          instructor.lessonTypes.find(
                            (lt: LessonType) => lt.priceNegotiable
                          )
                        ) {
                          return "가격 협의";
                        }
                        return instructor.pricePerHour > 0
                          ? `${instructor.pricePerHour.toLocaleString()}원/시간`
                          : LESSON_PRICE_DISPLAY_LABEL;
                      })()}
                </div>
              </div>

              {/* 정보 */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {instructor.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {instructor.tagline || instructor.specialty}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1 bg-yellow-50 px-2 py-1 rounded-lg">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-sm font-semibold text-gray-900">
                      {instructor.reviewCount > 0
                        ? instructor.rating.toFixed(1)
                        : "커넥톤 공식 프로"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center text-xs text-gray-500 space-x-3 mb-3">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-3 h-3" />
                    <span>{instructor.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span className="line-clamp-1">
                      {instructor.experience.split("\n")[0] ||
                        instructor.specialty}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mb-3">
                  {instructor.instruments.slice(0, 3).map((instrument, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center space-x-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium"
                    >
                      <Music className="w-3 h-3" />
                      <span>{instrument}</span>
                    </span>
                  ))}
                </div>

                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {instructor.bio}
                </p>

                <div className="flex flex-wrap gap-1">
                  {instructor.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {nextSlotByInstructor[instructor.id]
                      ? `다음 가능 ${nextSlotByInstructor[instructor.id]}`
                      : `리뷰 ${instructor.reviewCount}개`}
                  </span>
                  <span className="text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                    자세히 보기 →
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* 결과 없음 */}
        {sortedInstructors.length === 0 && (
          <div className="text-center py-12">
            <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              검색 결과가 없습니다
            </h3>
            <p className="text-gray-600">다른 검색어나 필터를 시도해보세요</p>
          </div>
        )}

        {/* 강사 상세 모달 */}
        <InstructorModal
          instructor={selectedInstructor as any}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onBookLesson={(instructor, lessonType, slot) => {
            void handleBookLesson(
              instructor as Instructor,
              lessonType as LessonType,
              slot
            );
          }}
        />
      </div>
    </div>
  );
}
